import { useRef, useState } from "react";
import { Trash2, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Input } from "../ui/input";
import { PROJECTS } from "../../data/projects";
import { useProjectPhotos } from "../../lib/photos";
import { api, formatApiError } from "../../lib/api";

export const PhotoManager = () => {
  const [slug, setSlug] = useState(PROJECTS[0].slug);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const qc = useQueryClient();
  const { data: photos = [], isLoading } = useProjectPhotos(slug);
  const project = PROJECTS.find((p) => p.slug === slug);
  const refresh = () => qc.invalidateQueries({ queryKey: ["project-photos"] });

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    let ok = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("project_slug", slug);
      fd.append("caption", caption);
      fd.append("file", file);
      try {
        await api.post("/project-photos", fd);
        ok += 1;
      } catch (err) {
        toast.error(`${file.name}: ${formatApiError(err.response?.data?.detail)}`);
      }
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
    if (ok) toast.success(`${ok} photo${ok > 1 ? "s" : ""} added to ${project.short}`);
    refresh();
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo from the project page?")) return;
    try {
      await api.delete(`/project-photos/${id}`);
      toast.success("Photo removed");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <section data-testid="photo-manager">
      <div className="grid gap-4 border border-black/10 bg-white p-6 md:grid-cols-3">
        <label className="space-y-1.5 text-sm font-medium">
          <span>Project</span>
          <select value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="photo-project-select" className="h-10 w-full border border-black/20 bg-white px-3">
            {PROJECTS.map((p) => <option key={p.slug} value={p.slug}>{p.short}</option>)}
          </select>
        </label>
        <label className="space-y-1.5 text-sm font-medium">
          <span>Caption (optional, used for all selected photos)</span>
          <Input value={caption} maxLength={200} onChange={(e) => setCaption(e.target.value)} data-testid="photo-caption-input" className="rounded-none" placeholder="e.g. Barricade rails after installation" />
        </label>
        <div className="flex items-end">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="photo-file-input" />
          <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="photo-upload-button" className="btn-rust flex h-10 w-full items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {busy ? "Uploading..." : "Upload photos"}
          </button>
        </div>
      </div>
      <p className="mt-6 text-sm text-[var(--muted-ink)]">{project.images.length} built-in photos and {photos.length} uploaded photos are shown on this project page.</p>
      {isLoading ? <Loader2 className="mt-6 h-6 w-6 animate-spin" /> : (
        <div className="mt-6 grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {photos.map((p) => (
            <figure key={p.id} data-testid="uploaded-photo" className="relative bg-white">
              <img src={p.src} alt={p.alt} className="aspect-[4/3] w-full object-cover" />
              <figcaption className="truncate p-2 text-xs text-[var(--muted-ink)]">{p.caption || "No caption"}</figcaption>
              <button type="button" onClick={() => remove(p.id)} aria-label="Delete photo" data-testid={`delete-photo-${p.id}`} className="absolute right-2 top-2 bg-white/90 p-1.5 text-red-700 hover:bg-white">
                <Trash2 className="h-4 w-4" />
              </button>
            </figure>
          ))}
          {photos.length === 0 && <p data-testid="uploaded-photos-empty" className="col-span-full text-sm text-[var(--muted-ink)]">No uploaded photos for this project yet.</p>}
        </div>
      )}
    </section>
  );
};
